import {
  ArrowUpRight,
  CloudRain,
  Hammer,
  Layers,
  LineChart,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { localizePath, type LocaleCode, useI18n } from "@/i18n";
import { serviceBySlug } from "@/config/services";

const ICONS = [Layers, Wrench, Hammer, ShieldCheck, CloudRain, LineChart];

const CARD_SERVICE_SLUGS = [
  "",
  "emergency-roof-leak-repair",
  "asphalt-shingle-replacement",
  "metal-roof-replacement",
  "flat-roof-replacement-sbs-tpo-pvc",
  "moss-removal-roof-gutter",
] as const;

const ALL_SERVICES_LABEL: Record<LocaleCode, string> = {
  "en-ca": "View All Roofing Services",
  "zh-cn": "查看全部屋顶服务",
  "zh-tw": "查看全部屋頂服務",
  "pa-in": "ਸਾਰੀਆਂ ਛੱਤ ਸੇਵਾਵਾਂ ਵੇਖੋ",
  "ko-kr": "전체 지붕 서비스 보기",
  "fa-ir": "مشاهدهٔ همهٔ خدمات سقف",
  "ar-sa": "عرض جميع خدمات الأسقف",
};

export function Services() {
  const { t, locale } = useI18n();

  return (
    <section id="services" className="section-y">
      <div className="container-page">
        <SectionHeader
          eyebrow={t.services.eyebrow}
          title={t.services.title}
          body={t.services.body}
        />

        <ul className="mt-12 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {t.services.items.map((item, i) => {
            const Icon = ICONS[i % ICONS.length]!;
            const service = serviceBySlug[CARD_SERVICE_SLUGS[i] ?? CARD_SERVICE_SLUGS[0]];
            return (
              <li
                key={item.title}
                className="group bg-card p-7 transition-colors hover:bg-muted/60"
              >
                <Icon className="size-5 text-accent" aria-hidden="true" />
                <p className="mt-6 text-xs tracking-[0.12em] uppercase text-muted-foreground">
                  {item.intent}
                </p>
                <h3 className="display-3 mt-2 text-foreground">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                <a
                  href={localizePath(service?.path ?? "/services", locale)}
                  aria-label={`${t.cta.learnMore}: ${item.title}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {t.cta.learnMore}
                  <ArrowUpRight
                    className="size-4 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </a>
              </li>
            );
          })}
        </ul>

        <div className="mt-10 flex justify-center">
          <a
            href={localizePath("/services", locale)}
            className="group inline-flex items-center gap-2 rounded-md border border-foreground px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            {ALL_SERVICES_LABEL[locale]}
            <ArrowUpRight
              className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              aria-hidden="true"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
